import { apiClient } from '../../../shared/api/client';
import {
  AnonymousSurveyInstanceDTO,
  SurveyResponseDTO,
  CreateSurveyResponseDTO,
  SubmitAnswerDTO,
  BatchSubmitAnswersDTO,
  SurveyResponseSessionDTO,
  SurveyResponseProgressDTO,
  SurveyResponseSummaryDTO,
} from '../dtos';

// Base URLs for public survey endpoints (no auth required)
const PUBLIC_BASE_URL = '/public/surveys';

/**
 * Get public survey instance data (no authentication required)
 */
export const getPublicSurveyInstance = async (
  instanceId: string,
  surveyId: string
): Promise<AnonymousSurveyInstanceDTO> => {
  // Use efficient route pattern with surveyId
  const url = `${PUBLIC_BASE_URL}/${surveyId}/instances/${instanceId}`;

  const response = await apiClient.get<AnonymousSurveyInstanceDTO>(url);
  if (!response.data) {
    throw new Error('No survey instance data received');
  }
  return response.data;
};

/**
 * Create anonymous survey response session
 */
export const createAnonymousSession = async (
  instanceId: string,
  sessionData: CreateSurveyResponseDTO,
  surveyId: string
): Promise<SurveyResponseSessionDTO> => {
  const url = `${PUBLIC_BASE_URL}/${surveyId}/instances/${instanceId}/sessions`;

  const response = await apiClient.post<SurveyResponseSessionDTO>(url, sessionData);
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};

/**
 * Submit answer for a single question
 */
export const submitAnswer = async (
  instanceId: string,
  sessionId: string,
  answerData: SubmitAnswerDTO,
  surveyId: string
): Promise<SurveyResponseProgressDTO> => {
  const url = `${PUBLIC_BASE_URL}/${surveyId}/instances/${instanceId}/sessions/${sessionId}/answers`;

  const response = await apiClient.post<SurveyResponseProgressDTO>(url, answerData);
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};

/**
 * Submit multiple answers at once
 */
export const submitBatchAnswers = async (
  instanceId: string,
  sessionId: string,
  batchData: BatchSubmitAnswersDTO,
  surveyId: string
): Promise<SurveyResponseProgressDTO> => {
  const url = `${PUBLIC_BASE_URL}/${surveyId}/instances/${instanceId}/sessions/${sessionId}/answers/batch`;

  const response = await apiClient.post<SurveyResponseProgressDTO>(url, batchData);
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};

/**
 * Complete survey response
 */
export const completeSurveyResponse = async (
  instanceId: string,
  sessionId: string,
  surveyId: string
): Promise<SurveyResponseSummaryDTO> => {
  const url = `${PUBLIC_BASE_URL}/${surveyId}/instances/${instanceId}/sessions/${sessionId}/complete`;

  const response = await apiClient.post<SurveyResponseSummaryDTO>(url);
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};

/**
 * Get current survey response data
 */
export const getSurveyResponse = async (
  instanceId: string,
  sessionId: string,
  surveyId: string
): Promise<SurveyResponseDTO> => {
  const response = await apiClient.get<SurveyResponseDTO>(
    `${PUBLIC_BASE_URL}/${surveyId}/instances/${instanceId}/sessions/${sessionId}/response`
  );
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};
